//
// Web Components.
// Audio.
// -------------------

import * as EVENTS from './audio-constants.js'
import { getItemDetails, readableTime } from './audio-functions.js'

class AudioPlayer {

  /**
   * @param {HTMLElement} root 
   */
  constructor(root) {
    this.root = root
    this.audio = new Audio()

    this.root.tabIndex = 0

    this.audio.loop = false
    this.audio.preload = 'auto'
    this.audio.controls = false
    this.audio.autoplay = false

    this.current = parseInt(this.root.dataset.current || 0, 10)

    this.href = this.root.querySelector('.audio-player__href')
    this.image = this.root.querySelector('.audio-player__image')
    this.title = this.root.querySelector('.audio-player__title')
    this.artist = this.root.querySelector('.audio-player__artist')

    this.buttonPlay = this.root.querySelector('.audio-player__button--play')
    this.buttonNext = this.root.querySelector('.audio-player__button--next')
    this.buttonPrev = this.root.querySelector('.audio-player__button--prev')

    this.progress = this.root.querySelector('.audio-player__progress')
    this.progressBuffer = this.root.querySelector('.audio-player__progress-buffer')
    this.progressRunnable = this.root.querySelector('.audio-player__progress-runnable')

    this.timeCurrent = this.root.querySelector('.audio-player__time-current')
    this.timeDuration = this.root.querySelector('.audio-player__time-duration')

    this.volume = this.root.querySelector('.audio-player__volume-slider')

    this.items = Array.from(this.root.querySelectorAll('.audio-player__item'))
    this.playlist = this.root.querySelector('.audio-player__playlist')
    this.menu = this.playlist.querySelector('.audio-player__menu')

    this.progress.setAttribute('aria-valuemin', 0)
    this.progress.setAttribute('aria-valuenow', 0)

    this.audio.volume = this.volume.value = Number(this.root.dataset.volume || 3) / 10

    this.setSelectedItem_(this.items[this.current])

    this._endedEventHandler = () => this.endedHandler_()
    this._canPlayEventHandler = () => this.canPlayHandler_()
    this._bufferEventHandler = event => this.bufferHandler_(event)
    this._timeUpdateEventHandler = () => this.timeUpdateHandler_()
    this._durationChangeEventHandler = () => this.durationChangeHandler_()

    this.audio.addEventListener('ended', this._endedEventHandler)
    this.audio.addEventListener('canplay', this._canPlayEventHandler)
    this.audio.addEventListener('progress', this._bufferEventHandler)
    this.audio.addEventListener('timeupdate', this._timeUpdateEventHandler)
    this.audio.addEventListener('durationchange', this._durationChangeEventHandler)

    this._nextEventHandler = () => this.next()
    this._prevEventHandler = () => this.prev()
    this._toggleEventHandler = () => this.toggle()
    this._keyDownEventHandler = event => this.keyDownHandler_(event)
    this._keyPressEventHandler = event => this.keyPressNextPrevHandler_(event)
    this._volumeEventHandler = event => (this.audio.volume = event.target.value)
    this._mouseDownSeekEventHandler = event => this.mouseDownSeekHandler_(event)

    this.root.addEventListener('keydown', this._keyDownEventHandler)
    this.root.addEventListener('keypress', this._keyPressEventHandler)

    this.volume.addEventListener('input', this._volumeEventHandler)
    
    this.buttonNext.addEventListener('click', this._nextEventHandler)
    this.buttonPrev.addEventListener('click', this._prevEventHandler)
    this.buttonPlay.addEventListener('click', this._toggleEventHandler)

    EVENTS.MOUSEDOWN.forEach(
      name => this.progress.addEventListener(
        name, this._mouseDownSeekEventHandler
      )
    )

    if (this.items.length >= 1) this.items.forEach(
      (item, index) => item.addEventListener(
        'click', () => this.itemClickHandler_(index)
      )
    )
  }

  /**
   * @returns {void}
   */
  next() {
    let c = this.current
    let i = this.items

    ++this.current

    if (this.current > i.length - 1)
      this.current = 0

    this.setSelectedItem_(i[this.current], i[c])

    if (this.audio.paused) this.play()
  }

  /**
   * @returns {void}
   */
  pause() {
    if (this.audio.paused) return

    this.audio.pause()
    this.root.classList.remove('audio-player--is-playing')
    this.buttonPlay.setAttribute('aria-label', 'Play (k)')
  }

  /**
   * @returns {void}
   */
  play() {
    if ( ! this.audio.paused) return

    this.audio.play()
    this.root.classList.add('audio-player--is-playing')
    this.buttonPlay.setAttribute('aria-label', 'Pause (k)')
  }

  /**
   * @returns {void}
   */
  prev() {
    let c = this.current, i = this.items

    --this.current

    if (this.current < 0)
      this.current = i.length - 1

    this.setSelectedItem_(i[this.current], i[c])

    if (this.audio.paused) this.play()
  }

  /**
   * @returns {void}
   */
  toggle() {
    if (this.audio.paused)
      this.play()
    else this.pause()
  }

  /**
   * @param {Event} event
   * @returns {void}
   */
  bufferHandler_(event) {
    if (
      this.audio.buffered &&
      this.audio.buffered.length > 0 &&
      this.audio.buffered.end &&
      this.audio.duration
    ) this.progressBuffer.style.setProperty(
      'transform',
      `scaleX(${1 * (event.target.buffered.end(0) / event.target.duration)})`
    )
  }

  /**
   * @returns {void}
   */
  canPlayHandler_() {
    this.root.classList.add('audio-player--can-play')

    this.progress.setAttribute(
      'aria-valuemax', Math.round(this.audio.duration)
    )
  }

  /**
   * @returns {void}
   */
  durationChangeHandler_() {
    this.timeDuration.innerHTML = readableTime(this.audio.duration)
  }

  /**
   * @returns {void}
   */
  endedHandler_() {
    this.audio.pause()
    this.next()
  }

  /**
   * @param {number} index
   * @returns {void}
   */
  itemClickHandler_(index) {
    if (this.current === index) return

    let current = this.items[this.current]
    let next = this.items[index]

    this.current = index

    this.setSelectedItem_(next, current)

    if (this.audio.paused) this.play()
  }

  /**
   * @param {Event} event
   * @returns {void}
   */
  keyDownHandler_(event) {
    if (event.type !== 'keydown') return

    const { key, keyCode } = event

    if (
      key === EVENTS.KEYS.KEY_K ||
      keyCode === EVENTS.KEYCODES.KEY_K
    ) return this.toggle()

    if (
      key === EVENTS.KEYS.ENTER ||
      keyCode === EVENTS.KEYCODES.ENTER ||
      key === EVENTS.KEYS.SPACE_BAR ||
      keyCode === EVENTS.KEYCODES.SPACE_BAR
    ) return this.keyDownPlayPauseHandler_(event)

    if (
      key === EVENTS.KEYS.ARROW_RIGHT ||
      keyCode === EVENTS.KEYCODES.ARROW_RIGHT ||
      key === EVENTS.KEYS.ARROW_LEFT ||
      keyCode === EVENTS.KEYCODES.ARROW_LEFT
    ) return this.keyDownSeekHandler_(event)

    if (
      key === EVENTS.KEYS.ARROW_UP ||
      keyCode === EVENTS.KEYCODES.ARROW_UP ||
      key === EVENTS.KEYS.ARROW_DOWN ||
      keyCode === EVENTS.KEYCODES.ARROW_DOWN
    ) return this.keyDownVolumeHandler_(event)
    
    if (
      key === EVENTS.KEYS.KEY_N ||
      keyCode === EVENTS.KEYCODES.KEY_N ||
      key === EVENTS.KEYS.KEY_P ||
      keyCode === EVENTS.KEYCODES.KEY_P
    ) return this.keyPressNextPrevHandler_(event)
  }

  /**
   * @param {Event} event
   * @returns {void}
   */
  keyDownPlayPauseHandler_(event) {
    if (
      event.target === this.buttonPlay ||
      event.target === this.href
    ) return false

    if (event) event.preventDefault()

    return this.toggle()
  }

  /**
   * @param {Event} event
   * @returns {void}
   */
  keyDownSeekHandler_(event) {
    if (
      event.target === this.volume ||
      event.target.parentElement === this.menu ||
      event.target === this.menu
    ) return false

    let c = this.audio.currentTime
    let d = this.audio.duration

    const { key, keyCode } = event

    if (
      key === EVENTS.KEYS.ARROW_RIGHT ||
      keyCode === EVENTS.KEYCODES.ARROW_RIGHT
    ) c = c + 10

    if (
      key === EVENTS.KEYS.ARROW_LEFT ||
      keyCode === EVENTS.KEYCODES.ARROW_LEFT
    ) c = c - 10

    if (c >= d) c = d
    if (c <= 0) c = 0

    this.audio.currentTime = c
  }

  /**
   * @param {Event} event
   * @returns {void}
   */
  keyDownVolumeHandler_(event) {
    if (event.target === this.volume)
      return false

    if (event) event.preventDefault()

    let v = this.audio.volume * 10

    const { key, keyCode } = event

    if (
      key === EVENTS.KEYS.ARROW_UP ||
      keyCode === EVENTS.KEYCODES.ARROW_UP
    ) v = v + 0.1

    if (
      key === EVENTS.KEYS.ARROW_DOWN ||
      keyCode === EVENTS.KEYCODES.ARROW_DOWN
    ) v = v - 0.1

    if (v >= 10) v = 10
    if (v <= 0) v = 0

    this.audio.volume = this.volume.value = v / 10
  }

  /**
   * @param {Event} event
   * @returns {void}
   */
  keyPressNextPrevHandler_(event) {
    if (event.type !== 'keypress') return

    const { key, keyCode, shiftKey } = event

    if ( ! shiftKey) return
    
    if (
      key === EVENTS.KEYS.KEY_N ||
      keyCode === EVENTS.KEYCODES.KEY_N
    ) return this.next()

    if (
      key === EVENTS.KEYS.KEY_P ||
      keyCode === EVENTS.KEYCODES.KEY_P
    ) return this.prev()
  }

  /**
   * @param {Event} event
   * @returns {void}
   */
  mouseDownSeekHandler_(event) {
    const pause = this.root.classList.contains('audio-player--is-playing')

    if (pause) this.audio.pause()

    const moveHandler = e => {
      let { value } = this.progressBasedOnMousePosition_(e)

      this.audio.currentTime = value
    }

    const upHandler = () => {
      if (pause) this.audio.play()

      document.removeEventListener(EVENTS.MOUSEMOVE[event.type], moveHandler)
      document.removeEventListener(EVENTS.MOUSEUP[event.type], upHandler)
    }
  
    moveHandler(event)

    document.addEventListener(EVENTS.MOUSEMOVE[event.type], moveHandler)
    document.addEventListener(EVENTS.MOUSEUP[event.type], upHandler)
  }

  /**
   * @param {HTMLElement} item
   * @param {HTMLElement | void} [previous]
   * @returns {void}
   */
  setSelectedItem_(item, previous = null) {
    let details = getItemDetails(item)

    if ( ! details.src || details.src === '')
      return console.error('Error: A valid audio file is required!')

    item.classList.add('audio-player__item--selected')
    item.setAttribute('aria-selected', 'true')
    item.tabIndex = -1

    if (previous) {
      previous.classList.remove('audio-player__item--selected')
      previous.removeAttribute('aria-selected')
      previous.tabIndex = 0
    }

    this.menu.scrollTo({
      left: item.offsetLeft - item.offsetWidth,
      behavior: 'smooth'
    })

    this.audio.src = details.src
    this.audio.load()

    this.timeCurrent.textContent = '00:00'

    this.href.href = details.href
    this.image.src = details.image
    this.title.textContent = details.title
    this.artist.textContent = details.artist
  }

  /**
   * @param {Event} event
   * @returns {{ duration: number, offset: number, value: number }}
   */
  progressBasedOnMousePosition_(event) {
    let x = event.changedTouches ? event.changedTouches[0].pageX : event.clientX
    let duration = this.audio.duration
    let offset = (x - this.progress.offsetLeft) / this.progress.offsetWidth 
    let value = (offset * duration)

    if (value >= duration) value = duration
    if (value <= 0) value = 0

    return { duration, offset, value }
  }

  /**
   * @returns {void}
   */
  timeUpdateHandler_() {
    let t = this.audio.currentTime
    let p = t / this.audio.duration

    this.timeCurrent.innerHTML = readableTime(t)
    this.progress.setAttribute('aria-valuenow', Math.round(t))
    this.progressRunnable.style.setProperty('transform', `scaleX(${p})`)
  }

}

;(document => {
  const audio = document.querySelector('.audio-player')
  if (audio) new AudioPlayer(audio)
})(document)
